import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Server, Lightbulb, Code, HardHat } from 'lucide-react';

const capabilitiesData = [
  { 
    id: 1, 
    title: 'Multi-Tenant SaaS Architecture', 
    category: 'Architecture', 
    desc: 'Blueprint for highly efficient, secure, and scalable multi-tenant platforms using shared infrastructure and isolated data schemas.', 
    tags: ['Microservices', 'PostgreSQL', 'Isolation'] 
  },
  { 
    id: 2, 
    title: 'Real-time Data Processing PoC', 
    category: 'Proof of Concept', 
    desc: 'Demonstration of WebSocket integration using Node.js for low-latency updates required by trading or collaborative applications.', 
    tags: ['Node.js', 'WebSockets', 'Latency'] 
  },
  { 
    id: 3, 
    title: 'Cloud Deployment Blueprints', 
    category: 'DevOps', 
    desc: 'Terraform and AWS infrastructure-as-code templates for instant, reliable, and secure cloud environments.', 
    tags: ['AWS', 'Terraform', 'CI/CD'] 
  },
  { 
    id: 4, 
    title: 'Secure API Gateways', 
    category: 'Architecture', 
    desc: 'Implementing OAuth 2.0 and JWT security layers for protecting critical backend service endpoints.', 
    tags: ['OAuth 2.0', 'JWT', 'Security'] 
  },
  { 
    id: 5, 
    title: 'Flutter UI Components Library', 
    category: 'Proof of Concept', 
    desc: 'A showcase of custom, pixel-perfect widgets demonstrating advanced mobile UI/UX capabilities across iOS and Android.', 
    tags: ['Flutter', 'Mobile', 'UI/UX'] 
  },
  { 
    id: 6, 
    title: 'Performance Optimization Strategy', 
    category: 'Methodology', 
    desc: 'Detailed strategy for code-splitting, lazy-loading, and server-side rendering (SSR) to achieve high core web vitals.', 
    tags: ['React', 'SSR', 'Speed'] 
  },
];

const categories = ['All', 'Architecture', 'Proof of Concept', 'DevOps', 'Methodology'];

const Capabilities = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  // Filtering is now handled purely by React's state logic
  const filteredItems = activeCategory === 'All' 
    ? capabilitiesData 
    : capabilitiesData.filter(item => item.category === activeCategory);

  const getIcon = (category) => {
    switch(category) {
      case 'Architecture': return <Server size={24} className="text-primary" />;
      case 'Proof of Concept': return <Lightbulb size={24} className="text-primary" />;
      case 'DevOps': return <HardHat size={24} className="text-primary" />;
      case 'Methodology': return <CheckCircle size={24} className="text-primary" />;
      default: return <Code size={24} className="text-primary" />;
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <div className="pt-24 pb-20 bg-gradient-to-b from-slate-50 to-white min-h-screen relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle, #1060BE 2px, transparent 2px)`,
          backgroundSize: '50px 50px',
          backgroundPosition: '50px 50px'
        }} />
      </div>

      <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 mb-6 tracking-tight"
          >
            Technical Capabilities & Blueprints
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
          >
            Foundational expertise that powers every project. Proven blueprints for secure, scalable, and maintainable software systems.
          </motion.p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="flex flex-wrap gap-3 mb-12"
        >
          {categories.map((cat, index) => (
            <motion.button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                activeCategory === cat 
                ? 'bg-primary text-white shadow-lg' 
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 shadow-sm hover:shadow-md'
              }`}
            >
              {cat}
            </motion.button>
          ))}
        </motion.div>

        {/* Grid Container */}
        <motion.div
          layout
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="wait">
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                layout
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                whileHover={{ 
                  y: -12,
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
                className="p-6 rounded-2xl bg-gradient-to-br from-white to-slate-50 border border-slate-200 shadow-lg hover:shadow-xl space-y-4 group cursor-default relative transition-all duration-300"
              >
                {/* Category Badge & Icon */}
                <motion.div 
                  className="flex items-center gap-3"
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    className="p-2.5 bg-primary/10 rounded-lg"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {getIcon(item.category)}
                  </motion.div>
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{item.category}</span>
                </motion.div>
                
                {/* Title */}
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight group-hover:text-primary transition-colors">
                  {item.title}
                </h3>
                
                {/* Description */}
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {item.tags.map((tag, tagIndex) => (
                    <motion.span
                      key={tag}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: tagIndex * 0.05 }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 text-slate-700 rounded-lg shadow-sm hover:shadow-md cursor-default hover:border-primary/30 transition-all"
                    >
                      {tag}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Capabilities;